interface ILocationParams {
    pathname: string;
}

const SEGMENT_REGEX = /[^/]+/g;

export const LocationService = {
    /**
     * Извлекает имя сущности (последний сегмент) из переданного пути URL.
     *
     * @param location - Объект с информацией о местоположении (например, `window.location`).
     * @param location.pathname - Путь URL, из которого извлекается имя сущности.
     * @returns Имя сущности, извлеченное из URL, или пустая строка, если имя не найдено.
     */
    getEntityNameFromLocation: ({pathname}: ILocationParams): string => {
        const matches = pathname.match(SEGMENT_REGEX);
        return matches?.[matches.length - 1] || '';
    },

    /**
     * Извлекает код подраздела (последний сегмент с префиксом app.) из переданного пути URL.
     */
    getSubsectionFromLocation: ({pathname}: ILocationParams): string => {
        const entity = LocationService.getEntityNameFromLocation({pathname});
        return entity ? `app.${entity}` : '';
    },

    /**
     * Извлекает имя сущности из пути URL, игнорируя числовые ID в конце
     *
     * Функция анализирует путь URL и возвращает соответствующее имя сущности:
     * - Если последняя часть пути является числовым ID, возвращает предпоследнюю часть
     * - Если последняя часть не является числовой, возвращает последнюю часть
     *
     * @param path - Путь URL, из которого извлекается имя сущности.
     * @returns Имя сущности, извлеченное из URL, или пустая строка, если имя не найдено.
     *
     * @example
     * // Возвращает 'users' (игнорирует числовой ID)
     * getTabEntityNameFromLocation('/users/123');
     * @example
     * // Возвращает 'categories' (игнорирует числовой ID в длинном пути)
     * getTabEntityNameFromLocation('app/shop/category/products/categories/456');
     * @example
     * // Возвращает 'settings' (единственная нечисловая часть)
     * getTabEntityNameFromLocation('/settings');
     */
    getTabEntityNameFromLocation: (path?: string | null): string | undefined => {
        if (!path) return '';

        const parts = path.split('/').filter(part => part !== '');

        if (parts.length === 0) return '';

        const lastPart = parts[parts.length - 1];
        const isLastPartNumeric = /^\d+$/.test(lastPart);

        // Если последняя часть - число, возвращаем предпоследнюю
        // Если последняя часть не число, возвращаем последнюю
        if (isLastPartNumeric && parts.length >= 2) {
            return parts[parts.length - 2];
        }

        return lastPart;
    },
};
